import React, { createContext, useState } from "react";
import type { I18nContextValue, Language, Translations } from "./types";
import deTranslations from "./translations/de.json";
import enTranslations from "./translations/en.json";

/**
 * I18n Context for sharing language state across the application
 */
export const I18nContext = createContext<I18nContextValue | undefined>(
  undefined
);

/**
 * Translation map containing all language translations
 */
const translations: Record<Language, Translations> = {
  de: deTranslations as Translations,
  en: enTranslations as Translations,
};

/**
 * Detects the user's browser language and returns a supported language
 * Defaults to German if browser language is not supported
 *
 * @returns The detected language ('de' or 'en')
 */
function detectBrowserLanguage(): Language {
  const browserLang = navigator.language.toLowerCase();

  // Check for exact match (e.g., 'en', 'de')
  if (browserLang === "en" || browserLang.startsWith("en-")) {
    return "en";
  }

  if (browserLang === "de" || browserLang.startsWith("de-")) {
    return "de";
  }

  // Default to German
  return "de";
}

/**
 * Gets a nested translation value from the translations object
 * Supports dot notation like "common.buttons.continue"
 *
 * @param obj - The translations object
 * @param path - The dot-notation path to the translation
 * @returns The translation string or the path if not found
 */
function getNestedValue(obj: any, path: string): string {
  const keys = path.split(".");
  let current = obj;

  for (const key of keys) {
    if (current && typeof current === "object" && key in current) {
      current = current[key];
    } else {
      // Translation not found, return the key as fallback
      console.warn(`Translation not found for key: ${path}`);
      return path;
    }
  }

  return typeof current === "string" ? current : path;
}

/**
 * I18n Provider component that manages language state and provides translation functionality
 *
 * Features:
 * - Detects browser language on first load
 * - Persists language choice in localStorage
 * - Provides translation function with parameter interpolation
 * - Supports language switching
 *
 * @param props - Component props
 * @param props.children - Child components that will have access to i18n context
 */
export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Language>(() => {
    // Try to load saved language from localStorage
    const saved = localStorage.getItem("aiio_language");
    if (saved === "de" || saved === "en") {
      return saved;
    }

    // Detect browser language
    const detected = detectBrowserLanguage();
    localStorage.setItem("aiio_language", detected);
    return detected;
  });

  /**
   * Changes the application language and persists the choice
   *
   * @param lang - The language to switch to
   */
  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem("aiio_language", lang);
  };

  /**
   * Translation function that retrieves a translation by key
   * Supports parameter interpolation with {param} syntax
   *
   * @param key - The translation key in dot notation (e.g., "common.buttons.continue")
   * @param params - Optional parameters for interpolation
   * @returns The translated string
   *
   * @example
   * t("documentUpload.selection.selectedFile", { filename: "document.pdf" })
   * // Returns: "Selected: document.pdf" (en) or "Ausgewählt: document.pdf" (de)
   */
  const t = (key: string, params?: Record<string, string | number>): string => {
    let translation = getNestedValue(translations[language], key);

    // Handle parameter interpolation
    if (params) {
      Object.keys(params).forEach((paramKey) => {
        translation = translation.replace(
          new RegExp(`\\{${paramKey}\\}`, "g"),
          String(params[paramKey])
        );
      });
    }

    return translation;
  };

  const value: I18nContextValue = {
    language,
    setLanguage,
    t,
  };

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
